<template>
  <div :class="{ 'mobile-user-reviews': !isDesktop, 'desktop-user-reviews': isDesktop }">
    <h2 class="user-reviews-title">{{ t("user_reviews_title") }}</h2>
    <div class="user-reviews-container">
      <button class="nav-button nav-button-left" @click="scrollLeft" :disabled="isScrolling">
        <i class="iconfont icon-icon_arrowsleft"></i>
      </button>

      <div class="reviews-wrapper" ref="wrapperRef">
        <div class="reviews-track" ref="trackRef" :style="{ transform: `translateX(${translateX}px)` }">
          <div v-for="(review, index) in displayReviews" :key="`${review.id}-${index}`" class="review-card">
            <div class="review-rating">
              <i v-for="star in 5" :key="star" class="iconfont"
                :class="star <= review.rating ? 'icon-icon_star' : 'icon-icon_star1'"></i>
            </div>
            <p class="review-text">{{ review.text }}</p>
            <div class="review-author">
              <span class="review-name">{{ review.name }}</span>
              <span class="review-verified" v-if="review.verified">
                <i class="iconfont icon-icon_protect_1"></i>
                {{ t("user_reviews_verified") }}
              </span>
              <!-- <span 
                v-if="review.verified" 
                class="review-linkedin"
              >
                {{ t("reviews_linkedin") }}
              </span> -->
            </div>
            <div class="review-title">{{ review.title }}</div>
          </div>
        </div>
      </div>

      <button class="nav-button nav-button-right" @click="scrollRight" :disabled="isScrolling">
        <i class="iconfont icon-icon_arrowsrigh"></i>
      </button>
    </div>
    <div class="user-reviews-more" @click="handleMore">{{ t("reviews_more") }}2,847,392{{ t("reviews_more_count") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import useUserStore from '~/store/modules/user'
const { t, locale } = useI18n()
// 统一基准 URL，读取环境变量，末尾不含斜杠
const BASE_URL = (useRuntimeConfig().public.pdfOneUrl as string)?.replace(/\/$/, '') || 'https://pdf1.com'
const router = useRouter()
const userStore = useUserStore()
const { reviews } = storeToRefs(userStore)
const wrapperRef = ref<HTMLElement>()
const trackRef = ref<HTMLElement>()
const translateX = ref(0)
const isScrolling = ref(false)
const cardWidth = ref(0)
const gap = 20 // 卡片之间的间距


watch(locale, () => {
  userStore.getReviews(locale.value).then(() => {
    nextTick(() => {
      updateCardWidth()
    })
  })
})

// 获取前8个评论（如果不够8个就按数组数量来）
const limitedReviews = computed(() => {
  if (reviews.value.length === 0) {
    return []
  }
  return reviews.value.slice(0, Math.min(8, reviews.value.length))
})

// 创建显示用的评价数组（用于走马灯无限循环）
// 结构：[左侧副本] + [中间显示] + [右侧副本]
// 初始位置在中间部分的开始位置
const displayReviews = computed(() => {
  const reviewsList = limitedReviews.value
  if (reviewsList.length === 0) {
    return []
  }

  // 为了无限循环，创建3份副本
  return [
    ...reviewsList, // 左侧副本
    ...reviewsList, // 中间显示部分（初始位置）
    ...reviewsList  // 右侧副本
  ]
})

// 初始位置：显示中间部分的开始（索引 = limitedReviews.value.length）
const currentIndex = ref(0)

const updateCardWidth = () => {
  if (trackRef.value && wrapperRef.value && limitedReviews.value.length > 0) {
    const firstCard = trackRef.value.querySelector('.review-card') as HTMLElement
    if (firstCard) {
      if (isDesktop.value) {
        cardWidth.value = firstCard.offsetWidth + gap
      } else {
        cardWidth.value = wrapperRef.value.offsetWidth
      }
      const initialIndex = limitedReviews.value.length
      currentIndex.value = initialIndex
      translateX.value = -currentIndex.value * cardWidth.value
    }
  }
}

const handleMore = () => {
  console.log('handleMore')
  router.push('/reviews')
}

onMounted(() => {
  console.log('reviews==', reviews.value)

  if (reviews.value.length == 0) {
    userStore.getReviews(locale.value).then(() => {
      nextTick(() => {
        updateCardWidth()
      })
    })
  } else {
    nextTick(() => {
      updateCardWidth()
    })
  }

  // 监听窗口大小变化
  window.addEventListener('resize', updateCardWidth)

  // 监听 reviews 数据变化，重新初始化位置
  watch(() => limitedReviews.value.length, () => {
    nextTick(() => {
      updateCardWidth()
    })
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', updateCardWidth)
})

// GEO 优化：动态注入 AggregateRating + Review Schema
// AI 引擎（Google AI Overview、Perplexity）偏向引用有用户评价数据的产品
useHead(computed(() => {
  if (limitedReviews.value.length === 0) return {}
  const totalRating = limitedReviews.value.reduce((sum: number, r: any) => sum + (r.rating || 5), 0)
  const avgRating = (totalRating / limitedReviews.value.length).toFixed(1)
  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'PDF-one',
          url: BASE_URL,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: avgRating,
            reviewCount: '2847392',
            bestRating: '5',
            worstRating: '1',
          },
          review: limitedReviews.value.slice(0, 5).map((r: any) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name || 'PDF-one User' },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: String(r.rating || 5),
              bestRating: '5',
            },
            reviewBody: r.text || '',
            name: r.title || '',
          })),
        }),
      },
    ],
  }
}))


// 向右滚动（走马灯效果）
const scrollRight = () => {
  if (isScrolling.value || !cardWidth.value || limitedReviews.value.length === 0) return

  const reviewsCount = limitedReviews.value.length
  isScrolling.value = true
  currentIndex.value++
  translateX.value = -currentIndex.value * cardWidth.value

  // 如果滚动到右侧副本（索引 >= 2 * reviewsCount），立即重置到中间部分开始
  // 中间部分从索引 reviewsCount 开始，到 2 * reviewsCount - 1 结束
  if (currentIndex.value >= 2 * reviewsCount) {
    setTimeout(() => {
      if (trackRef.value) {
        // 移除过渡效果，实现瞬间跳转
        trackRef.value.style.transition = 'none'
        currentIndex.value = reviewsCount
        translateX.value = -currentIndex.value * cardWidth.value
        // 恢复过渡效果
        requestAnimationFrame(() => {
          if (trackRef.value) {
            trackRef.value.style.transition = ''
          }
          isScrolling.value = false
        })
      }
    }, 300) // 等待动画完成
  } else {
    setTimeout(() => {
      isScrolling.value = false
    }, 300)
  }
}

// 向左滚动（走马灯效果）
const scrollLeft = () => {
  if (isScrolling.value || !cardWidth.value || limitedReviews.value.length === 0) return

  const reviewsCount = limitedReviews.value.length
  isScrolling.value = true
  currentIndex.value--
  translateX.value = -currentIndex.value * cardWidth.value

  // 如果滚动到左侧副本（索引 < reviewsCount），立即重置到中间部分的末尾
  // 中间部分从索引 reviewsCount 开始，到 2 * reviewsCount - 1 结束
  if (currentIndex.value < reviewsCount) {
    setTimeout(() => {
      if (trackRef.value) {
        // 移除过渡效果，实现瞬间跳转
        trackRef.value.style.transition = 'none'
        currentIndex.value = 2 * reviewsCount - 1
        translateX.value = -currentIndex.value * cardWidth.value
        // 恢复过渡效果
        requestAnimationFrame(() => {
          if (trackRef.value) {
            trackRef.value.style.transition = ''
          }
          isScrolling.value = false
        })
      }
    }, 300) // 等待动画完成
  } else {
    setTimeout(() => {
      isScrolling.value = false
    }, 300)
  }
}

const viewport = useViewport()
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))
</script>

<style scoped lang="scss">
@use './index.scss' as *;
@use './mobile.scss' as *;
</style>
